import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment as env } from '../../environments/environment';
import { AuthenticationService } from './authentication.service';

export interface DuelCreatedEvent {
	duelId: string; // secret id of the duel
	// challengerId: string;
	challengerUsername: string;
	// competitorId: string;
	// competitorUsername: string;
	// amount: number;
	// createdAt: string;
}

export interface DuelAcceptedEvent {
	duelId: string;
	acceptedBy: string;
	acceptedByUsername: string;
	acceptedAt: string;
}

export interface DuelDeclinedEvent {
	duelId: string;
}

@Injectable({
	providedIn: 'root',
})
export class SocketService {
	private socket: Socket | null = null;
	private socketUrl: string = `${env.ssl ? 'https' : 'http'}://${env.restServer}:${env.restServerPort}`;
	private userId: string | null = null;

	// BehaviorSubjects for reactive state
	private _isConnected = new BehaviorSubject<boolean>(false);

	// Public observables
	public readonly isConnected$: Observable<boolean> = this._isConnected.asObservable();
	public readonly duelCreatedSuccess$ = new Subject<string>();
	public readonly duelAcceptedSuccess$ = new Subject<string>();
	public readonly paymentSuccess$ = new Subject<{ amount: number }>();
	public readonly paymentCancelled$ = new Subject<string>();
	public readonly duelCreated$ = new Subject<DuelCreatedEvent>();
	public readonly duelAccepted$ = new Subject<string>();
	public readonly duelDeclined$ = new Subject<string>();
	public readonly duelUpdated$ = new Subject<string>();

	constructor(private authService: AuthenticationService) {
		// Store userId when user authenticates
		this.authService.authUser$.subscribe(user => {
			this.userId = user?.userId || null;
		});
	}

	// ---------------------------------------------------------------------------
	// @ Public Methods
	// ---------------------------------------------------------------------------

	/**
	 * Initialize and connect to the socket server
	 * Automatically registers the user upon successful connection
	 */
	connect(): void {
		if (this.socket?.connected) {
			// console.log('Socket already connected');
			return;
		}

		this.socket = io(this.socketUrl, {
			transports: ['websocket', 'polling'],
			reconnection: true,
			reconnectionDelay: 1000,
			reconnectionAttempts: 5,
		});

		this.setupEventListeners();
	}

	/**
	 * Disconnect from the socket server
	 */
	disconnect(): void {
		if (this.socket) {
			this.socket.disconnect();
			this.socket = null;
			this._isConnected.next(false);
			console.log('Socket disconnected');
		}
	}

	/**
	 * Emit a custom event to the server
	 */
	emit(eventName: string, data: unknown): void {
		if (!this.socket?.connected) {
			console.error('Cannot emit event: Socket not connected');
			return;
		}
		this.socket.emit(eventName, data);
	}

	/**
	 * Listen to a custom event from the server
	 * Returns a callback function that can be used to register a listener
	 */
	on(eventName: string, callback: (data: unknown) => void): () => void {
		if (!this.socket) {
			console.error('Socket not initialized');
			return () => {};
		}

		this.socket.on(eventName, callback);

		// Return cleanup function
		return () => {
			this.socket?.off(eventName, callback);
		};
	}

	// ---------------------------------------------------------------------------
	// @ Private Methods
	// ---------------------------------------------------------------------------

	/**
	 * Setup all socket event listeners
	 */
	private setupEventListeners(): void {
		if (!this.socket) return;

		// Connection events
		this.socket.on('connect', () => {
			console.log('Socket connected successfully');
			this._isConnected.next(true);

			// Auto-register user when connected
			if (!this.userId) {
				console.error('Cannot register user: userId is null');
				return;
			}
			this.socket?.emit('register', this.userId);
			console.log('User registered on socket server:', this.userId);
		});

		this.socket.on('disconnect', (reason: string) => {
			console.log('Socket disconnected:', reason);
			this._isConnected.next(false);
		});

		this.socket.on('connect_error', (error: Error) => {
			console.error('Socket connection error:', error);
			this._isConnected.next(false);
		});

		this.socket.on('reconnect', (attemptNumber: number) => {
			console.log('Socket reconnected after', attemptNumber, 'attempts');
			this._isConnected.next(true);

			// Re-register user after reconnection
			if (!this.userId) {
				console.error('Cannot register user: userId is null');
				return;
			}
			this.socket?.emit('register', this.userId);
			console.log('User re-registered on socket server after reconnect:', this.userId);
		});

		this.socket.on('reconnect_error', (error: Error) => {
			console.error('Socket reconnection error:', error);
		});

		this.socket.on('reconnect_failed', () => {
			console.error('Socket reconnection failed');
			this._isConnected.next(false);
		});

		// ---------------------------------------------------------------------------
		// @ Socket Event Methods
		// ---------------------------------------------------------------------------

		this.socket.on('credits-updated', (newCredits: number) => {
			console.log('Credits updated event received:', newCredits);
			this.authService.updateUserCredits(newCredits);
		});

		this.socket.on('payment-success', (data: { amount: number }) => {
			console.log('Payment success event received:', data);
			this.paymentSuccess$.next(data);
			// TODO: Handle credits update here
		});

		this.socket.on('duel-created-success', (duelSecretId: string) => {
			console.log('Duel created event received:', duelSecretId);
			this.duelCreatedSuccess$.next(duelSecretId);
		});

		this.socket.on('duel-accepted-success', (duelSecretId: string) => {
			console.log('Duel accepted event received:', duelSecretId);
			this.duelAcceptedSuccess$.next(duelSecretId);
		});

		this.socket.on('duel-created', (data: DuelCreatedEvent) => {
			console.log('Duel created event received:', data);
			this.duelCreated$.next(data);
		});

		this.socket.on('duel-accepted', (duelSecretId: string) => {
			console.log('Duel accepted event received:', duelSecretId);
			this.duelAccepted$.next(duelSecretId);
		});

		this.socket.on('duel-declined', (duelSecretId: string) => {
			console.log('Duel declined event received:', duelSecretId);
			this.duelDeclined$.next(duelSecretId);
		});

		this.socket.on('duel-updated', (duelSecretId: string) => {
			console.log('Duel updated event received:', duelSecretId);
			this.duelUpdated$.next(duelSecretId);
		});

		this.socket.on('payment-cancelled', (data: string) => {
			console.log('Payment cancelled event received:', data);
			this.paymentCancelled$.next(data);
		});
	}
}
